import {
  cloneElement,
  CSSProperties,
  ReactElement,
  useEffect,
  useRef,
} from 'react';
import { useVirtualGridStateContext } from './VirtualGridStateContext';
import { addMutationObserver } from '@wix/bex-core';

export function VirtualTableRowMutated(props: {
  index: number;
  style?: CSSProperties;
  itemKey?: (index: number) => number | string;
  rowIndex?: number;
  children: ReactElement[];
}) {
  const { children, index, style, itemKey, rowIndex } = props;

  const gridState = useVirtualGridStateContext();
  const element = children[index];

  const rowRef = useRef<HTMLTableRowElement | null | undefined>(null);

  const setVariableRowHeight = () => {
    if (rowRef.current == null || gridState.calcGridProps.rowHeight != null) {
      return;
    }
    rowRef.current.style.height = 'auto';

    if (itemKey == null) {
      return;
    }

    gridState.setVariableRowHeight?.({
      key: itemKey?.(index),
      size: rowRef.current?.offsetHeight,
    });

    rowRef.current.style.height =
      gridState.calcGridProps.rowHeightIncludingGap?.({
        index,
        key: itemKey(index),
      }) + 'px';
  };

  useEffect(() => {
    setVariableRowHeight();
  }, [rowRef, rowIndex]);

  useEffect(() => {
    if (!rowRef.current) {
      return;
    }

    const disposer = addMutationObserver(rowRef.current, () => {
      setVariableRowHeight();
    });

    return () => {
      disposer?.();
    };
  }, [rowRef.current]);

  if (element == null) {
    return null;
  }

  return cloneElement(element, {
    style: { ...(element.props.style ?? {}), ...style },
    rowProps: {
      ...(element.props.rowProps ?? {}),
      'data-key':
        process.env.NODE_ENV === 'development' ? itemKey?.(index) : undefined,
      'data-index': `index-${index}`,
      ref: (el: HTMLTableRowElement | null | undefined) => {
        rowRef.current = el;
      },
    },
  });
}
